import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent, ScrollView } from "react-native";
import { View, useDevice } from "@lookiero/aurora";
import { useI18n } from "@lookiero/i18n-react";
import { DOMAIN } from "../../i18n/i18n";
import { Offering } from "../../views/offeringHeader/OfferingHeader";
import { useActiveOffering } from "../../views/offeringHeader/useActiveOffering/useActiveOffering";
import { PillVariant } from "../pill/Pill";
import { initialScrollOffset, PillLayout, PillsViewport, scrollOffsetToRevealPill } from "./OfferingsPills.controller";
import { style as offeringsPillsStyle } from "./OfferingsPills.style";
import { OfferingPill } from "./offeringPill/OfferingPill";

const SCROLL_X = 20;

interface OfferingsPillsProps {
  readonly offerings: Offering[];
}

const OfferingsPills: FC<OfferingsPillsProps> = ({ offerings }) => {
  const { formatMessage } = useI18n({ domain: DOMAIN });

  const style = useMemo(() => offeringsPillsStyle(), []);

  const activeOffering = useActiveOffering();

  const { screen } = useDevice();
  const wrapperStyle = useMemo(() => {
    if (screen.L) {
      return style.contentContainerLarge;
    }
    if (screen.M) {
      return style.contentContainerMedium;
    }
    if (screen.S) {
      return style.contentContainerSmall;
    }

    return null;
  }, [
    screen.L,
    screen.M,
    screen.S,
    style.contentContainerLarge,
    style.contentContainerMedium,
    style.contentContainerSmall,
  ]);

  const enabledOfferingsCount = useMemo(() => offerings.filter(({ enabled }) => enabled).length, [offerings]);

  const listRef = useRef<ScrollView>(null);
  const pillLayouts = useRef<Record<number, PillLayout>>({});
  const viewport = useRef<PillsViewport>({ offset: 0, width: 0 });
  const contentWidth = useRef(0);
  const initialScrollDone = useRef(false);

  const activeIndex = offerings.findIndex(({ offering }) => offering === activeOffering);

  const handleInitialScroll = useCallback(() => {
    if (initialScrollDone.current || activeIndex < 0 || !contentWidth.current) {
      return;
    }

    const pill = pillLayouts.current[activeIndex];

    if (!pill) {
      return;
    }

    initialScrollDone.current = true;

    const offset = initialScrollOffset({ pill, index: activeIndex, margin: SCROLL_X });

    if (offset !== undefined) {
      listRef.current?.scrollTo({ x: offset, animated: false });
    }
  }, [activeIndex]);

  const handleOnPillLayout = useCallback(
    (index: number, event: LayoutChangeEvent) => {
      const { x, width } = event.nativeEvent.layout;
      pillLayouts.current[index] = { x, width };

      handleInitialScroll();
    },
    [handleInitialScroll],
  );

  const handleOnContentSizeChange = useCallback(
    (width: number) => {
      contentWidth.current = width;

      handleInitialScroll();
    },
    [handleInitialScroll],
  );

  const handleOnLayout = useCallback((event: LayoutChangeEvent) => {
    viewport.current = { ...viewport.current, width: event.nativeEvent.layout.width };
  }, []);

  const handleOnScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    viewport.current = { ...viewport.current, offset: event.nativeEvent.contentOffset.x };
  }, []);

  useEffect(() => {
    if (!initialScrollDone.current) {
      handleInitialScroll();

      return;
    }

    const offset = scrollOffsetToRevealPill({
      pill: pillLayouts.current[activeIndex],
      viewport: viewport.current,
      margin: SCROLL_X,
    });

    if (offset !== undefined) {
      listRef.current?.scrollTo({ x: offset, animated: false });
    }
  }, [activeIndex, handleInitialScroll]);

  if (enabledOfferingsCount <= 1) {
    return null;
  }

  return (
    <View>
      <ScrollView
        ref={listRef}
        contentContainerStyle={[style.contentContainer, wrapperStyle]}
        showsHorizontalScrollIndicator={false}
        style={style.container}
        horizontal
        scrollEventThrottle={16}
        onContentSizeChange={handleOnContentSizeChange}
        onLayout={handleOnLayout}
        onScroll={handleOnScroll}
      >
        {offerings.map(({ enabled, offering, text, onPress }, index) => (
          <View key={offering} onLayout={(event) => handleOnPillLayout(index, event)}>
            <OfferingPill
              isActive={offering === activeOffering}
              text={formatMessage({ id: text })}
              variant={PillVariant.BASIC}
              visible={enabled}
              onPress={onPress}
            />
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

export { OfferingsPills };
